import { atom, AtomEffect } from 'recoil';

export type ThemeMode = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'admin-panel-theme';

/** Read the persisted theme choice; defaults to `system` (follow the OS). */
export const loadThemeMode = (): ThemeMode => {
	try {
		const value = localStorage.getItem(THEME_STORAGE_KEY);
		return value === 'light' || value === 'dark' || value === 'system'
			? value
			: 'system';
	} catch {
		return 'system';
	}
};

/**
 * Reflect the chosen mode onto <html data-theme>. `system` maps to `auto`, which the CSS
 * pairs with `@media (prefers-color-scheme: dark)` so it tracks the OS. The same write is
 * done by an inline script in index.html for the first paint (no flash) — this keeps it in
 * sync on every subsequent change.
 */
export const applyThemeMode = (mode: ThemeMode): void => {
	try {
		document.documentElement.setAttribute(
			'data-theme',
			mode === 'system' ? 'auto' : mode
		);
	} catch {
		/* SSR / non-DOM guard */
	}
};

const persistAndApplyEffect: AtomEffect<ThemeMode> = ({ onSet }) => {
	// Keep the DOM aligned with the persisted value when the atom is first used.
	applyThemeMode(loadThemeMode());
	onSet(newValue => {
		try {
			localStorage.setItem(THEME_STORAGE_KEY, newValue);
		} catch {
			/* storage unavailable — still apply for this session */
		}
		applyThemeMode(newValue);
	});
};

/** Local-only theme preference (light | dark | system). No backend; persisted in localStorage. */
export const themeModeState = atom<ThemeMode>({
	key: 'theme-mode-state',
	default: loadThemeMode(),
	effects: [persistAndApplyEffect],
});
